import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  startIntegrationBridgeFilter,
  startIntegrationBridgeFilterOptions,
  startIntegrationBridgeFilterSchema,
} from '../../app/actions/semanticIntegrationActions';
import { isIntegrationFilterBarEnabled } from '../../app/helpers/semanticSearchConfigHelper';

const emptyComboValues = (combos = []) => {
  const values = {};
  combos.forEach((combo) => {
    values[combo.field_name] = '';
  });
  return values;
};

const hasAnyFilter = (likeText, comboValues) =>
  Boolean(
    String(likeText || '').trim() ||
      Object.keys(comboValues || {}).some((k) => comboValues[k] != null && comboValues[k] !== '')
  );

const dropStaleComboValues = (comboValues, byField) => {
  const next = { ...comboValues };
  Object.keys(next).forEach((field) => {
    if (!next[field]) return;
    const opts = byField[field] || [];
    const still = opts.some(
      (opt) => String(opt.id) === String(next[field]) || String(opt.code) === String(next[field])
    );
    if (!still) next[field] = '';
  });
  return next;
};

const comboOptionValue = (opt) => (opt.code != null ? opt.code : opt.id);

/**
 * Barra genérica: LIKE + combos activos (use_in_combo + use_in_bridge) sobre el universo M3/M4.
 */
const IntegrationFilterBar = ({
  targetModule,
  rawResults = [],
  onFilteredResults,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const configuration = useSelector((state) => state.configuration);
  const flagOn = isIntegrationFilterBarEnabled(configuration);

  const [schema, setSchema] = useState(null);
  const [options, setOptions] = useState({ byField: {}, series: [], ciiuLevel2: [], problems: [] });
  const [likeText, setLikeText] = useState('');
  const [comboValues, setComboValues] = useState({});
  const [applying, setApplying] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(true);

  const rawIds = useMemo(
    () => (rawResults || []).map((row) => row.id).filter(Boolean),
    [rawResults]
  );
  const rawKey = rawIds.join(',');
  const combos = schema?.combos || [];

  useEffect(() => {
    if (!flagOn || !targetModule) {
      setSchema(null);
      setSchemaLoading(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setSchemaLoading(true);
      const resp = await dispatch(startIntegrationBridgeFilterSchema(targetModule));
      if (!cancelled) {
        setSchema(resp?.ok ? resp.schema : null);
        setSchemaLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [flagOn, targetModule, dispatch]);

  const loadOptions = async (nextLike, nextCombos) => {
    if (!rawIds.length) {
      setOptions({ byField: {}, series: [], ciiuLevel2: [], problems: [] });
      return { byField: {} };
    }
    const resp = await dispatch(
      startIntegrationBridgeFilterOptions({
        moduleKey: targetModule,
        ids: rawIds,
        likeText: nextLike,
        comboValues: nextCombos,
      })
    );
    const nextOptions = resp?.ok
      ? resp.options || { byField: {} }
      : { byField: {} };
    if (!nextOptions.byField) nextOptions.byField = {};
    setOptions(nextOptions);
    return nextOptions;
  };

  useEffect(() => {
    if (!flagOn || !schema?.enabled || !rawIds.length) {
      setOptions({ byField: {}, series: [], ciiuLevel2: [], problems: [] });
      setLikeText('');
      setComboValues(emptyComboValues(schema?.combos || []));
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      const cleared = emptyComboValues(schema.combos || []);
      if (!cancelled) {
        setLikeText('');
        setComboValues(cleared);
      }
      const resp = await dispatch(
        startIntegrationBridgeFilterOptions({ moduleKey: targetModule, ids: rawIds })
      );
      if (!cancelled && resp?.ok) {
        const nextOptions = resp.options || { byField: {} };
        if (!nextOptions.byField) nextOptions.byField = {};
        setOptions(nextOptions);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagOn, schema?.enabled, targetModule, rawKey, dispatch, combos.length]);

  if (!flagOn || schemaLoading || !schema?.enabled) return null;

  const applyFilters = async (nextLike, nextCombos, { refreshCombos = true } = {}) => {
    if (!rawIds.length) {
      onFilteredResults(rawResults);
      return;
    }
    if (!hasAnyFilter(nextLike, nextCombos)) {
      onFilteredResults(rawResults);
      if (refreshCombos) await loadOptions('', emptyComboValues(combos));
      return;
    }
    setApplying(true);
    const resp = await dispatch(
      startIntegrationBridgeFilter({
        moduleKey: targetModule,
        ids: rawIds,
        likeText: nextLike,
        comboValues: nextCombos,
      })
    );
    if (!resp?.ok) {
      setApplying(false);
      return;
    }
    const allowed = new Set((resp.ids || []).map(Number));
    onFilteredResults(rawResults.filter((row) => allowed.has(Number(row.id))));

    if (refreshCombos) {
      const nextOptions = await loadOptions(nextLike, nextCombos);
      const sanitized = dropStaleComboValues(nextCombos, nextOptions.byField || {});
      const stale = Object.keys(sanitized).some((k) => sanitized[k] !== nextCombos[k]);
      if (stale) {
        setComboValues(sanitized);
        await applyFilters(nextLike, sanitized, { refreshCombos: false });
      }
    }
    setApplying(false);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    applyFilters(likeText, comboValues);
  };

  const onClear = async () => {
    const cleared = emptyComboValues(combos);
    setLikeText('');
    setComboValues(cleared);
    onFilteredResults(rawResults);
    setApplying(true);
    await loadOptions('', cleared);
    setApplying(false);
  };

  const likeCount = schema.likeText?.fields?.length || 0;
  const fieldHint = (schema.likeText?.fields || [])
    .slice(0, 8)
    .map((f) => f.field_name)
    .join(', ');
  const more = likeCount > 8 ? ` +${likeCount - 8}` : '';

  return (
    <Form className="integration-filter-bar" onSubmit={onSubmit}>
      <div className="integration-filter-bar__row">
        {schema.likeText?.enabled && (
          <Form.Group className="integration-filter-bar__field">
            <Form.Label>{t('semanticSearch.integration.filterBar.likeLabel')}</Form.Label>
            <Form.Control
              type="text"
              value={likeText}
              onChange={(event) => setLikeText(event.target.value)}
              placeholder={t(
                targetModule === 'financial'
                  ? 'semanticSearch.integration.filterBar.likePlaceholder'
                  : 'semanticSearch.integration.filterBar.likePlaceholderGeneric'
              )}
            />
          </Form.Group>
        )}

        {combos.map((combo) => (
          <Form.Group className="integration-filter-bar__field" key={combo.field_name}>
            <Form.Label>
              {combo.widget === 'ciiu_level'
                ? t('semanticSearch.integration.filterBar.ciiu', { level: combo.ciiu_level || 2 })
                : t(combo.label_key, { defaultValue: combo.field_name })}
            </Form.Label>
            <Form.Select
              value={comboValues[combo.field_name] || ''}
              onChange={(event) =>
                setComboValues((prev) => ({ ...prev, [combo.field_name]: event.target.value }))
              }
            >
              <option value="">{t('semanticSearch.integration.filterBar.all')}</option>
              {(options.byField?.[combo.field_name] || []).map((opt) => {
                const value = comboOptionValue(opt);
                return (
                  <option key={String(value)} value={value}>
                    {combo.widget === 'ciiu_level'
                      ? (opt.code && opt.name && opt.name !== opt.code
                        ? `${opt.code} — ${opt.name}`
                        : opt.name || opt.code || value)
                      : (String(opt.description || '').trim() || opt.name || opt.code || value)}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        ))}

        <div className="integration-filter-bar__actions">
          <Button type="submit" variant="primary" size="sm" disabled={applying || !rawIds.length}>
            {applying ? <Spinner animation="border" size="sm" /> : t('semanticSearch.integration.filterBar.apply')}
          </Button>
          <Button type="button" variant="outline-secondary" size="sm" onClick={onClear} disabled={applying}>
            {t('semanticSearch.integration.filterBar.clear')}
          </Button>
        </div>
      </div>
      {schema.likeText?.enabled && likeCount > 0 && (
        <Form.Text className="text-muted d-block mt-1" title={`${fieldHint}${more}`}>
          {t('semanticSearch.integration.filterBar.likeHint', { count: likeCount })}
        </Form.Text>
      )}
    </Form>
  );
};

export default IntegrationFilterBar;
